import { useMutation } from '@tanstack/react-query';

import { useApiRequestState } from '../use-api-request-state';
import { API_REQUEST_MUTATION_KEY } from '../constants';

/**
 * Generic hook for handling API requests with abort controller management
 */
export const useApiRequest = <TVariables = void, TData = void>(
  requestId: string,
  mutationFn: (variables: TVariables, signal: AbortSignal) => Promise<TData>,
  options?: {
    onSuccess?: (data: TData) => void;
    onError?: (error: Error, variables: TVariables) => void;
  }
) => {
  const apiRequestState = useApiRequestState(requestId);
  const { registerAbortController, unregisterAbortController, isLoading, isError, error, cancelRequest, resetMutationState } =
    apiRequestState;

  const mutation = useMutation({
    mutationKey: [API_REQUEST_MUTATION_KEY, requestId],
    mutationFn: async (variables: TVariables) => {
      const abortController = new AbortController();
      registerAbortController(abortController);

      return await mutationFn(variables, abortController.signal);
    },
    onSuccess: options?.onSuccess,
    onError: (error, variables) => {
      options?.onError?.(error as Error, variables);
    },
    onSettled: () => {
      unregisterAbortController();
    },
  });

  return {
    mutate: mutation.mutateAsync,
    isLoading,
    isError,
    error,
    cancelRequest,
    resetMutationState,
  };
};
